import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { ProtectedRoute } from "../components/ProtectedRoute";
import { searchContracts, Contract } from "../api/dataverse";
import { dataverseScopes } from "../auth/msalConfig";

const municipios = [
  "Adjuntas","Aguada","Aguadilla","Aguas Buenas","Aibonito","Arecibo","Arroyo","Añasco",
  "Barceloneta","Barranquitas","Bayamón","Cabo Rojo","Caguas","Camuy","Canóvanas","Carolina",
  "Cataño","Cayey","Ceiba","Ciales","Cidra","Coamo","Comerío","Corozal","Culebra","Dorado",
  "Fajardo","Florida","Guánica","Guayama","Guayanilla","Guaynabo","Gurabo","Hatillo",
  "Hormigueros","Humacao","Isabela","Jayuya","Juana Díaz","Juncos","Lajas","Lares",
  "Las Marías","Las Piedras","Loíza","Luquillo","Manatí","Maricao","Maunabo","Mayagüez",
  "Moca","Morovis","Naguabo","Naranjito","Orocovis","Patillas","Peñuelas","Ponce",
  "Quebradillas","Rincón","Río Grande","Sabana Grande","Salinas","San Germán","San Juan",
  "San Lorenzo","San Sebastián","Santa Isabel","Toa Alta","Toa Baja","Trujillo Alto",
  "Utuado","Vega Alta","Vega Baja","Vieques","Villalba","Yabucoa","Yauco",
];

const ContractSearchContent: React.FC = () => {
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [query, setQuery] = useState("");
  const [municipio, setMunicipio] = useState("all");
  const [expiringDays, setExpiringDays] = useState<number | null>(null);
  const [activoFilter, setActivoFilter] = useState<boolean | null>(null);
  const { instance } = useMsal();
  const navigate = useNavigate();

  const load = (
    q: string,
    muni: string,
    expDays: number | null,
    activo: boolean | null
  ) => {
    setLoading(true);
    setSearched(true);
    instance
      .acquireTokenSilent({ scopes: dataverseScopes, account: instance.getActiveAccount() || undefined })
      .then((r) =>
        searchContracts(r.accessToken, q, muni, {
          expiringDays: expDays ?? undefined,
          activo: activo ?? undefined,
        })
      )
      .then(setContracts)
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    load(query, municipio, expiringDays, activoFilter);
  };

  const applyExpiringFilter = (days: number | null) => {
    setExpiringDays(days);
    load(query, municipio, days, activoFilter);
  };

  const applyActivoFilter = (activo: boolean | null) => {
    setActivoFilter(activo);
    load(query, municipio, expiringDays, activo);
  };

  const handleClear = () => {
    setQuery("");
    setMunicipio("all");
    setExpiringDays(null);
    setActivoFilter(null);
    setSearched(false);
    setContracts([]);
  };

  const formatDate = (iso: string) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("es-PR", { year: "numeric", month: "short", day: "numeric" });
  };

  const formatCurrency = (n: number) =>
    n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 });

  const daysUntilExpiry = (fecha: string) => {
    if (!fecha) return null;
    return Math.floor((new Date(fecha).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  };

  const expiryBadge = (days: number | null) => {
    if (days === null) return null;
    if (days < 0)    return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">Vencido ({Math.abs(days)}d)</span>;
    if (days <= 30)  return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">{days} días</span>;
    if (days <= 60)  return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">{days} días</span>;
    if (days <= 90)  return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">{days} días</span>;
    return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">{days} días</span>;
  };

  const expiringFilter = [
    { label: "Todos", value: null },
    { label: "Vence en 30d", value: 30 },
    { label: "Vence en 60d", value: 60 },
    { label: "Vence en 90d", value: 90 },
  ];

  const activoFilterOptions = [
    { label: "Todos", value: null },
    { label: "Activos", value: true },
    { label: "Inactivos", value: false },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Contratos</h1>
        <p className="text-gray-500 mt-1">Busque por número de contrato, cliente o use los filtros rápidos</p>
      </div>

      {/* Search bar */}
      <form onSubmit={handleSearchSubmit} className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Número de contrato o nombre de cliente..."
            className="w-full px-4 py-3 pl-11 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
          />
          <svg className="absolute left-3.5 top-3.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <select
          value={municipio}
          onChange={(e) => setMunicipio(e.target.value)}
          className="px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
        >
          <option value="all">Todos los municipios</option>
          {municipios.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <button
          type="submit"
          disabled={loading}
          className="px-6 py-3 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 transition shadow-sm disabled:opacity-50"
        >
          {loading ? "Buscando..." : "Buscar"}
        </button>
        {searched && (
          <button
            type="button"
            onClick={handleClear}
            className="px-4 py-3 bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200 transition text-sm"
          >
            Limpiar
          </button>
        )}
      </form>

      {/* Quick filters */}
      <div className="flex flex-wrap gap-4 mb-6 items-center">
        {/* Expiring filter */}
        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-500 mr-1">Vencimiento:</span>
          {expiringFilter.map((f) => (
            <button
              key={String(f.value)}
              onClick={() => applyExpiringFilter(f.value)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                expiringDays === f.value
                  ? "bg-blue-600 text-white"
                  : "bg-white border border-gray-200 text-gray-600 hover:border-blue-400"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="w-px h-6 bg-gray-200" />

        {/* Active/inactive toggle */}
        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-500 mr-1">Estado:</span>
          {activoFilterOptions.map((f) => (
            <button
              key={String(f.value)}
              onClick={() => applyActivoFilter(f.value)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                activoFilter === f.value
                  ? "bg-blue-600 text-white"
                  : "bg-white border border-gray-200 text-gray-600 hover:border-blue-400"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary stats */}
      {searched && contracts.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <div className="text-2xl font-bold text-gray-900">{contracts.length}</div>
            <div className="text-xs text-gray-500">Contratos</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <div className="text-2xl font-bold text-green-600">{contracts.filter(c => c.activo).length}</div>
            <div className="text-xs text-gray-500">Activos</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <div className="text-2xl font-bold text-blue-600 text-lg">
              {formatCurrency(contracts.reduce((s, c) => s + c.cuantia, 0))}
            </div>
            <div className="text-xs text-gray-500">Cuantía Total</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <div className="text-2xl font-bold text-red-600">
              {contracts.filter(c => { const d = daysUntilExpiry(c.fechaVencimiento); return d !== null && d <= 90; }).length}
            </div>
            <div className="text-xs text-gray-500">Vencen en 90 días</div>
          </div>
        </div>
      )}

      {/* Results */}
      {searched && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
            <span className="text-sm font-medium text-gray-700">
              {loading ? "Buscando..." : `${contracts.length} contratos encontrados`}
            </span>
          </div>

          {!loading && contracts.length === 0 ? (
            <div className="p-12 text-center text-gray-400">
              No se encontraron contratos con los criterios especificados
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Contrato</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Cliente</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Municipio</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Cuantía</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Vigencia</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Vence en</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Estado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {contracts.map((c) => {
                    const days = daysUntilExpiry(c.fechaVencimiento);
                    return (
                      <tr
                        key={c.id}
                        onClick={() => navigate(`/contratos/${c.contractId}`)}
                        className="hover:bg-blue-50 cursor-pointer transition"
                      >
                        <td className="px-4 py-3">
                          <div className="font-semibold text-blue-600 text-sm">{c.contractId}</div>
                          <div className="text-xs text-gray-400">{c.categoria}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">{c.clientName}</td>
                        <td className="px-4 py-3">
                          <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-indigo-50 text-indigo-700">
                            {c.municipio}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-semibold text-gray-900">
                          {formatCurrency(c.cuantia)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-xs text-gray-500">{formatDate(c.fechaContrato)}</div>
                          <div className="text-xs text-gray-500">→ {formatDate(c.fechaVencimiento)}</div>
                        </td>
                        <td className="px-4 py-3 text-center">{expiryBadge(days)}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                            c.activo ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
                          }`}>
                            {c.status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {!searched && (
        <div className="text-center py-16 text-gray-400">
          <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <p className="text-sm">Busque por contrato o cliente, o use los filtros rápidos</p>
        </div>
      )}
    </div>
  );
};

export const ContractSearchPage: React.FC = () => (
  <ProtectedRoute>
    <ContractSearchContent />
  </ProtectedRoute>
);
